const 
	MOVE_NONE = 0,
	MOVE_START = 1,
	MOVE_END = 2,
	MOVE_BOTH = 3,
	MOVE_START_FINE = 4,
	MOVE_END_FINE = 5,
	MOVE_GROUP = 6;

const MIN_DURATION = 5 * 60 * 1000;

class TimelineEvent {

	static fromArray(layer, data) {
		let inst = new TimelineEvent(layer);

		inst._id = data.shift();
		data.shift(); // layer id not required
		inst.activity = globalTimeline.activities.findById(data.shift());
		inst.startTime = data.shift() * 1000;
		inst.endTime = data.shift() * 1000;
		let repeat = data.shift();
		inst._repeat = Array.isArray(repeat) ? Repeat.fromArray(repeat, inst) : null;

		return inst;
	}

	static fromDrag(layer, activity, start, end) {
		let inst = new TimelineEvent(layer);

		inst.activity = activity;
		inst.startTime = start;
		inst.endTime = end;

		inst.activateOnSave = true;
		
		inst.build();
		return inst;
	}

	static fromInstanceWithoutRepeat(instance) {
		let event = new TimelineEvent(instance.event.layer);
		event.activity = instance.event.activity;
		event.startTime = instance.startTime;
		event.endTime = instance.endTime;
		
		return event;
	}

	static fromInstanceWithRepeat(instance) {
		let event = TimelineEvent.fromInstanceWithoutRepeat(instance);
		if (instance.event.isRepeated) {
			event._repeat = instance.event.repeat.copy(event, instance.rawStartTime);
		}
		return event;
	}

	constructor(layer) {
		this._id = 0;
		this._layer = layer;
		this._activity = null;
		this._startTime = 0;
		this._endTime = 0;
		this._repeat = null;

		this._millisecondsOfDay = 0;

		this._instances = new TimelineItemList('startTime', 'endTime');

		this._onMove = MOVE_NONE;
		this._moveTime = 0;

		this._handleRepeat = null;
		this._building = false;
		this._buildAgain = false;

		this.activateOnSave = false;
	}

	get id() {return this._id;}
	set id(value) {this._id = value;}

	get layer() {return this._layer;}
	set layer(value) {this._layer = value;}

	get activity() {return this._activity;}
	set activity(activity) {
		if (activity instanceof Activity) {
			this._activity = activity;
			activity.addEvent(this);
			this.onActivityChange();
		}
	}

	get repeat() {
		if (this._repeat === null) {
			this._repeat = Repeat.create(this);
		}
		return this._repeat; 
	}
	set repeat(newRepeat) {
		this._repeat = newRepeat;
	}

	get isRepeated() {
		return this._repeat !== null;
	}

	get isNotRepeated() {
		return !this.isRepeated;
	}

	get graphicId() {
		return this.activity.graphicId;
	}

	get instances() {
		return this._instances;
	}

	get color() {
		return this.activity.color;
	}

	get onMove() {
		return this._onMove;
	}
	set onMove(value) {
		this._onMove = value;
	}

	get startTime() {
		return this.onMove == MOVE_START || this.onMove == MOVE_BOTH ?
			this._startTime + this.moveTime : 
			this._startTime;
	}
	set startTime(value) {
		this._startTime = value;
		this._millisecondsOfDay = this._startTime % MILLISECONDS_PER_DAY;
	}

	get endTime() {
		return this.onMove == MOVE_END || this.onMove == MOVE_BOTH ? 
			this._endTime + this.moveTime : 
			this._endTime;
	}
	set endTime(value) {
		this._endTime = value;
	}

	get duration() {
		return this.endTime - this.startTime;
	}

	get moveTime() {
		return this._moveTime;
	}

	set withRepeat (value) {
		this._handleRepeat = value;
	}

	get shouldModifyRepeat() {
		return this._handleRepeat == REPEAT.UPDATE_SINGLE || this._handleRepeat == REPEAT.UPDATE_FORWARD;
	}

	addInstance(time) {
		if (this._instances.hasNext) {
			let instance = this._instances.moveNext();
			instance.reTime(time);
			this.layer.instances.add(instance);
		} else {
			let instance = new EventInstance(this, time);
			this._instances.add(instance);
			this.layer.addEventInstance(instance);
		}
	}

	trimInstances() {
		while (this._instances.hasNext) {
			this._instances.pop().removeViews();
		}
	}
	
	rebuild() {
		this.build(true);
		return this;
	}

	build(update) {
		let fromTime = globalTimeline.memoryTimeA;
		let toTime = globalTimeline.memoryTimeB;

		if (update) {
			this._instances.forEach(inst => {
				this.layer.instances.remove(inst);
			});
			this._instances.reset();
		}

		if (this._repeat == null) {
			this.addInstance(false);
			this.trimInstances();
			return;
		}

		let testTime;
		if (this._instances.isEmpty || update) {
			// jump directly to the target region instead of walking from start
			let targetDay = fromTime - this._millisecondsOfDay - this.duration;
			testTime = this._repeat.jumpTo(targetDay);
			if (!testTime) return;

			let loopCheck = 50;
			while(testTime + this.duration < fromTime && loopCheck--){
				testTime = this._repeat.getNext(testTime);
				if (!testTime || toTime < testTime) return;
			}
			if (!loopCheck) console.log('loop 1 in event.build');

			this.addInstance(testTime);
		} else {
			testTime = this._repeat.getPrev(this._instances.first.startTime);
			let loopCheck = 9999;
			while (testTime && loopCheck--) {
				if (testTime + this.duration < fromTime) break;

				let instance = new EventInstance(this, testTime);
				this._instances.add(instance);
				this.layer.addEventInstance(instance);
				testTime = this._repeat.getPrev(testTime);
			}
			if (!loopCheck) console.log('loop 2 in event.build');
		}

		if (update) {
			testTime = this._repeat.getNext(testTime);
		} else {
			testTime = this._repeat.getNext(this._instances.moveLast().startTime);
		}
		let loopCheck = 9999;
		while (testTime && loopCheck--) {
			if (toTime < testTime) break;
			this.addInstance(testTime);
			testTime = this._repeat.getNext(testTime);
		}
		if (!loopCheck) console.log('loop 3 in event.build');
		if (update) {
			this.trimInstances();
		}
		this.layer.instances.updateInView(globalTimeline.viewStartMilliseconds, globalTimeline.viewEndMilliseconds);
	}

	getRepeatOption() {
		if (this.isNotRepeated || this._handleRepeat !== null) return false;
		globalTimeline.splitDialog.open(
			((option) => {
				if (
					option == REPEAT.UPDATE_FORWARD
					&& this.instances.marked.isFirstInRepeat
				) {
					option = REPEAT.UPDATE_ALL;
				}
				this._handleRepeat = option;
			}).bind(this),
			(() => {
				this._handleRepeat = null;
			}).bind(this)
		);
		return true;
	}

	withActiveInstances(func) {
		if (typeof func !== 'function') return;

		if (this.isNotRepeated || this._handleRepeat === REPEAT.UPDATE_ALL) {
			this._instances.forEach(func);
		} else if (this._handleRepeat === REPEAT.UPDATE_SINGLE) {
			this._instances.withMarked(func);
		} else if (this._handleRepeat === REPEAT.UPDATE_FORWARD) {
			this._instances.forEachAfter(func);
		}
	}

	onActivityChange() {
		this._instances.forEach(inst => {
			if (inst.active) {
				globalTimeline.showEventDetails(inst, true);
			}
			inst._views.forEach(view => {
				view.onActivityChange();
			})
		});
	}

	beginFineMove(action, event) {
		if (this.getRepeatOption()) return;
		this._onMove = action;
		this.liftMovers();
		this.mapSpaces();
		startDrag(event, globalTimeline);
	}

	beginMove(action, event) {
		if (this.getRepeatOption()) return
		this._onMove = action;
		this.liftMovers();
		this.mapSpaces();
		startDrag(event, this.layer);
	}

	liftMovers() {
		this.withActiveInstances(inst => {
			this.layer.addMovingInstance(inst);
		});
	}
	
	mapSpaces() {
		this.withActiveInstances(inst => inst.mapSpaces());
	}

	move(moveTime) {
		this._moveTime = moveTime;
		this.withActiveInstances(inst => inst.resize());
		globalTimeline.updateEventDetails();
	}

	clicked() {
		this.withActiveInstances(inst => {
			this.layer.instances.add(inst);
		});
		this.finishMoving();
	}

	dropped() {
		if (this.shouldModifyRepeat) {
			let event = this.eventFromInstance();
			event.save();
			this.modifyRepeat(event);

			this.finishMoving();
		} else {
			this.withActiveInstances(inst => {
				inst.applyMove();
				this.layer.instances.add(inst);
			});
			if (this.isRepeated) this.repeat.startTime = this.startTime;
			this.startTime = this.startTime;
			this.endTime = this.endTime;
			this.finishMoving();
			this.save();
		}
		this.rebuild();
	}

	finishMoving() {
		this._onMove = MOVE_NONE;
		this._moveTime = 0;
	}
	
	swapActivity(activity) {
		if (this.shouldModifyRepeat) {
			let event = this.eventFromInstance();
			event.activity = activity;
			event.save();
			this.modifyRepeat(event);
		}
		else {
			this.activity = activity;
			this.save();
		}
		this.rebuild();
	}

	moveTo(dTime, newLayer) {
		if (this.shouldModifyRepeat) {
			this.modifyRepeat(this.copyTo(dTime, newLayer));
			this.rebuild();
			return;
		}

		this.startTime = this.startTime + dTime;
		this.endTime = this.endTime + dTime;
		if (this.isRepeated) this.repeat.startTime = this.startTime;

		if (this.layer !== newLayer && !newLayer.isReadOnly) {
			this.withActiveInstances(inst => this.layer.removeEventInstance(inst));
			this.layer = newLayer;
			this.instances.init();
		}
		this.save();
	}

	copyTo(dTime, newLayer) {
		let event = this.eventFromInstance();
		event.startTime = event.startTime + dTime;
		event.endTime = event.endTime + dTime;
		if (event.isRepeated) event.repeat.startTime = event.startTime;

		if (event.layer !== newLayer && !newLayer.isReadOnly) {
			event.layer = newLayer;
		}
		event.save();
		return event;
	}

	eventFromInstance() {
		if (this._handleRepeat == REPEAT.UPDATE_SINGLE) {
			return TimelineEvent.fromInstanceWithoutRepeat(this._instances.marked);
		} else if (this._handleRepeat == REPEAT.UPDATE_FORWARD) {
			return TimelineEvent.fromInstanceWithRepeat(this._instances.marked);
		}
		return TimelineEvent.fromInstanceWithRepeat(this._instances.first);
	}

	modifyRepeat(exceptionEvent) {
		if (this._handleRepeat == REPEAT.UPDATE_SINGLE) {
			this.repeat.makeException(this._instances.marked.rawStartTime, exceptionEvent);
		} else if (this._handleRepeat == REPEAT.UPDATE_FORWARD) {
			this.repeat.trim(this._instances.marked.rawStartTime);
		}
		if (exceptionEvent) {
			globalTimeline.activateInstance(exceptionEvent.instances.first);
		} else {
			globalTimeline.clearActive();
		}
	}

	save() {
		if (this.activateOnSave) {
			globalTimeline.activateInstance(this._instances.first);
			this.activateOnSave = false;
		}
		this.rebuild();
 		saveManager.queue(this, 'saveCall');
	}

	saveCall() {
		API.upsert('events', this.id, {
			'activityId': this.activity.id,
			'layerId': this.layer.id,
			'startTime': this.startTime / 1000,
			'endTime': this.endTime / 1000,
			'repeated': this._repeat ? 1 : 0,
			'handleRepeat': this._handleRepeat,
		})
		.then(reply => {
			showError(reply);
			if (reply.id) {
				this.id = reply.id;
				globalTimeline.addEvent(this);
			}	
			if (this.isRepeated) this._repeat.save();
			saveManager.nudge();
		});
	}

	delete() {
		if (this.shouldModifyRepeat) {
			this.modifyRepeat(null);
			globalTimeline.clearActive();
			this.rebuild();
			return;
		}

		globalTimeline.removeEvent(this);
		saveManager.queue(this, 'deleteCall');
	}

	deleteCall() {
		API.delete('events/'+this._id)
		.then(reply => {
			showError(reply);
			saveManager.nudge();
		});
	}

	offsetFrom(utc) {
		this.offset = this.startTime - utc;
	}
}